import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Row as TanTableRow, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import type { FirmsController } from "../../../api/src/firms/firms.controller";
import type { AssetClassRiskAllocation } from "../../../api/src/firms/firms.service";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import TabContainerWithTabs from "../components/TabContainer";
import EditableTableRow from "../components/Table/EditableTableRow";
import { Table, useTable } from "../components/Table/Table";
import {
  YesNoSingleFilter,
  multiSelectIncludes,
  textOrExactFilter,
  yesNoOptions,
} from "../components/Table/filters";
import {
  deserializeDate,
  useAuthenticatedMutationAsync,
  useAuthenticatedQuery,
} from "../lib/api";
import { useRoles } from "../lib/auth";
import { permissionAwareErrorNotification } from "../lib/errorNotifications";
import { processFileResponse } from "../lib/file";
import { formatPercent } from "../lib/numbers";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";
import FirmNav from "./FirmNav";

function useQueryExportRiskAllocationsAssetClass() {
  return useAuthenticatedQuery<
    UnpackResponse<FirmsController["exportRiskAllocationsForAssetClasses"]>,
    void
  >({
    queryKey: ["firm", "asset-class-risk-allocations", "export"],
    queryFn: async () => undefined,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
}

/**
 * The route this screen reads the firm's asset-class risk allocations from.
 *
 * Unversioned. APP-1563 paginated the route and moved it to `/v1`; on
 * 2026-09-29 the operator removed it from version 1 of the Firm API, so it
 * answers here again, for the console only
 * (`docs/adr/firm-domain-api-version-1.md`). It is also the FIRST element of
 * every react-query key `useAuthenticatedFetchAllPages` uses, so the
 * invalidation after a save reaches every page of the collection rather than
 * only the one whose url it happens to spell.
 *
 * The EXPORT above is unversioned too: `an-decision-01a088c0c2b38187` keeps
 * the `StreamableFile` exports out of the JSON API, so
 * `GET /firm/asset-class-risk-allocations/export` never moved.
 * `useAuthenticatedQuery` builds that url out of its react-query key, which is
 * why that key is left exactly as it stands.
 */
const RISK_ALLOCATIONS_ASSET_CLASS_PATH = "/firm/asset-class-risk-allocations";

/** What the route answers with: a page of rows and the pagination beside it. */
type RiskAllocationsResponse = SerializedObject<
  UnpackResponse<FirmsController["getAllRiskAllocationsForAssetClasses"]>
>;

type RiskAllocationsRow = AssetClassRiskAllocation;
type RiskAllocationRequest = {
  ratio: number;
  mode: "INSERT" | "UPDATE" | "DELETE";
};

const RiskAllocationsAssetClass = () => {
  const roles = useRoles();
  const [fields, setFields] = useState<Record<string, number>>({});
  const [riskAllocationsEdited, setRiskAllocations] = useState<
    AssetClassRiskAllocation[]
  >([]);
  const [changedRiskAllocations, setChangedRiskAllocations] = useState<
    Record<string, RiskAllocationRequest>
  >({});

  const onUpdateRiskAllocations = useCallback(
    (assetClass: string, value: number) => {
      setRiskAllocations(
        riskAllocationsEdited.map((riskAllocation) => {
          if (riskAllocation.assetClass !== assetClass) {
            return riskAllocation;
          }

          if (value !== riskAllocation.originalRatio) {
            setChangedRiskAllocations((changedRiskAllocations) => ({
              ...changedRiskAllocations,
              [riskAllocation.assetClass]: {
                ratio: value,
                mode:
                  riskAllocation.defaultRatio === value
                    ? "DELETE"
                    : riskAllocation.originalRatio ===
                        riskAllocation.defaultRatio
                      ? "INSERT"
                      : "UPDATE",
              },
            }));
          } else {
            setChangedRiskAllocations((changedRiskAllocations) => {
              delete changedRiskAllocations[riskAllocation.assetClass];
              return { ...changedRiskAllocations };
            });
          }

          return {
            ...riskAllocation,
            growth: value,
            defensive: 100 - value,
            isOverride: riskAllocation.defaultRatio !== value,
          };
        }),
      );
    },
    [riskAllocationsEdited],
  );

  const onChangeText = useCallback(
    (cellId: string, rowId: string, value: string) => {
      const updatedValue = +value;
      setFields({
        ...fields,
        [cellId]: updatedValue,
        [`${rowId}_${cellId.endsWith("growth") ? "defensive" : "growth"}`]:
          100 - updatedValue,
      });
    },
    [fields],
  );

  const onResetRow = useCallback(
    (row: TanTableRow<RiskAllocationsRow>) => {
      onUpdateRiskAllocations(
        row.original.assetClass,
        row.original.defaultRatio,
      );
    },
    [onUpdateRiskAllocations],
  );

  const onConfirmRow = useCallback(
    (row: TanTableRow<RiskAllocationsRow>) => {
      onUpdateRiskAllocations(row.id, fields[`${row.id}_growth`]);
    },
    [onUpdateRiskAllocations, fields],
  );

  const onEditRow = useCallback(
    (row: TanTableRow<RiskAllocationsRow>) => {
      const currentRow = riskAllocationsEdited.find(
        (riskAllocation) =>
          riskAllocation.assetClass === row.original.assetClass,
      );

      if (typeof currentRow === "undefined") {
        throw new Error("Row not found in table: ", { cause: row.id });
      }

      setFields({
        ...fields,
        [`${row.id}_growth`]: currentRow.growth,
        [`${row.id}_defensive`]: currentRow.defensive,
      });
    },
    [riskAllocationsEdited, fields],
  );

  const onDiscardRow = useCallback(
    (row: TanTableRow<RiskAllocationsRow>) => {
      setFields({
        ...fields,
        [`${row.id}_growth`]: row.original.growth,
        [`${row.id}_defensive`]: row.original.defensive,
      });
    },
    [fields],
  );

  const columnHelper = useMemo(
    () => createColumnHelper<RiskAllocationsRow>(),
    [],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("assetClass", {
        header: () => "Asset Class",
        // A link from a security's page sets `?assetClass=` (APP-1486).
        filterFn: textOrExactFilter,
      }),
      columnHelper.accessor("growth", {
        cell: (info) => formatPercent(info.getValue() / 100),
        header: () => "Growth",
        enableColumnFilter: false,
        meta: {
          editableComponent: (cell) => (
            <InputGroup style={{ width: "100px" }}>
              <Form.Control
                value={fields[cell.id]}
                onChange={(e) =>
                  onChangeText(cell.id, cell.row.id, e.target.value)
                }
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
          ),
        },
      }),
      columnHelper.accessor("defensive", {
        cell: (info) => formatPercent(info.getValue() / 100),
        header: () => "Defensive",
        enableColumnFilter: false,
        meta: {
          editableComponent: (cell) => (
            <InputGroup style={{ width: "100px" }}>
              <Form.Control
                value={fields[cell.id]}
                onChange={(e) =>
                  onChangeText(cell.id, cell.row.id, e.target.value)
                }
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
          ),
        },
      }),
      columnHelper.accessor(
        (row) => {
          const override = yesNoOptions.find(
            (a) => a.label === (row.growth !== row.defaultRatio ? "Yes" : "No"),
          );
          return override?.value.toString() ?? "";
        },
        {
          id: "override",
          cell: (info) => {
            const override = yesNoOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            return override?.label === "No" ? null : (
              <Badge bg="warning" text="dark">
                Override
              </Badge>
            );
          },
          header: () => "Override",
          filterFn: multiSelectIncludes,
          meta: {
            filterComponent: YesNoSingleFilter,
          },
        },
      ),
      columnHelper.accessor("updatedTime", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "Updated Time",
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        size: 120,
      }),
    ],
    [columnHelper, onChangeText, fields],
  );

  // The route answers a PAGE, not the whole collection (APP-1563), so one
  // response is never the answer on its own: the walk in
  // `useAuthenticatedFetchAllPages` keeps asking until it has as many rows as
  // the sibling key says the firm has. Nothing in `tsc` can catch getting this
  // wrong — the element type of a page and of the whole array are the same — so
  // `RiskAllocationsAssetClass.pagination.spec.tsx` is what holds it.
  const {
    rows: collectedRows,
    hasEveryRow: hasEveryRiskAllocation,
    isPendingFirstPage: isPendingFirstRiskAllocationsPage,
    reload: reloadRiskAllocations,
  } = useAuthenticatedFetchAllPages<RiskAllocationsResponse["data"][number]>(
    RISK_ALLOCATIONS_ASSET_CLASS_PATH,
  );

  const riskAllocations = useMemo(
    () =>
      collectedRows.map((riskAllocation) => ({
        ...riskAllocation,
        updatedTime: deserializeDate(riskAllocation.updatedTime),
      })),
    [collectedRows],
  );

  useEffect(() => {
    // Copied into the editable state only once the walk HOLDS every row. A copy
    // per page would hand the screen a partial collection to edit, and the
    // `fields` map built beside it would be missing the rows still outstanding
    // — so an Admin could open a row that the next page was about to replace.
    if (!hasEveryRiskAllocation) {
      return;
    }

    setRiskAllocations(riskAllocations);
    setFields(
      riskAllocations.reduce(
        (result, riskAllocation) => ({
          ...result,
          [`${riskAllocation.assetClass}_growth`]: riskAllocation.growth,
          [`${riskAllocation.assetClass}_defensive`]: riskAllocation.defensive,
        }),
        {},
      ),
    );
  }, [hasEveryRiskAllocation, riskAllocations]);

  // Each `?assetClass=` shows that asset class's row (APP-1486).
  const [searchParams] = useSearchParams();
  const initialColumnFilters = useMemo(() => {
    const assetClasses = searchParams.getAll("assetClass");
    return assetClasses.length > 0
      ? [{ id: "assetClass", value: assetClasses }]
      : [];
  }, [searchParams]);

  const { table } = useTable({
    columns: roles.includes("Admin")
      ? columns
      : columns.filter((column) => column.id !== "isOverride"),
    data: riskAllocationsEdited,
    getRowId: (row) => row.assetClass,
    initialState: {
      pagination: {
        pageSize: 20,
      },
      columnFilters: initialColumnFilters,
    },
    autoResetAll: false,
  });

  const updateRiskAllocations = useAuthenticatedMutationAsync<
    SerializedObject<
      UnpackResponse<FirmsController["updateRiskAllocationsForAssetClass"]>
    >
  >(
    RISK_ALLOCATIONS_ASSET_CLASS_PATH,
    async (data: Record<string, RiskAllocationRequest>) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );

  const handleDiscardChanges = useCallback(() => {
    setRiskAllocations(
      riskAllocations.map((riskAllocation) => ({
        ...riskAllocation,
        growth: riskAllocation.originalRatio,
        defensive: 100 - riskAllocation.originalRatio,
        isOverride:
          riskAllocation.originalRatio !== riskAllocation.defaultRatio,
      })),
    );
    setChangedRiskAllocations({});
  }, [riskAllocations]);

  const queryClient = useQueryClient();
  const notificationContext = useContext(NotificationContext);

  const onSubmit = useMutation({
    mutationFn: async () => {
      try {
        await updateRiskAllocations(changedRiskAllocations);
        // The write echoes a PAGE bounded by its own request (APP-1563), not
        // the collection. It was written into the read's cache as the whole
        // list before APP-1563 — under the key
        // `["/firm/asset-class-risk-allocations"]`, which is not any page's
        // key, since each page's key adds its offset — and doing that now
        // would present one page as the whole collection the moment the firm
        // has more asset classes than a page. So the path is invalidated instead, which matches every page's
        // key, and the pages already collected are dropped: the walk stops once
        // it holds them all, so invalidating alone would refetch nothing.
        queryClient.invalidateQueries({
          queryKey: [RISK_ALLOCATIONS_ASSET_CLASS_PATH],
        });
        reloadRiskAllocations();
        setChangedRiskAllocations({});
        notificationContext.pushNotification({
          id: "risk-allocations",
          header: "Risk Allocations Updated",
          body: "Firm risk allocation settings updated",
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save risk allocations details", err);
        // APP-1327: a 403 means the user lacks the Admin role required to save
        // this mapping — show a clear permissions message. Any other failure
        // keeps the generic copy so real errors are not masked as a permissions
        // problem.
        notificationContext.pushNotification(
          permissionAwareErrorNotification(
            err,
            "risk-allocations",
            "Failed to Save Risk Allocations",
            "Firm risk allocation settings not saved",
            "Risk Allocations",
          ),
        );
      }
    },
  });

  const { isLoading: isLoadingExport, refetch: refetchExport } =
    useQueryExportRiskAllocationsAssetClass();

  const onExport = useCallback(async () => {
    await refetchExport();
  }, [refetchExport]);

  return (
    <TabContainerWithTabs tabs={FirmNav}>
      <Row>
        <Col>
          <ButtonToolbar className="mb-3">
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Export"
                icon="/icons/folded-list.svg"
                onClick={onExport}
                disabled={isLoadingExport}
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row
        hidden={Object.keys(changedRiskAllocations).length <= 0}
        className="mb-1"
      >
        <Col className="d-grid gap-1" xl={6} md={12}>
          <Button
            className="mb-1"
            variant="success"
            onClick={() => onSubmit.mutateAsync()}
            disabled={onSubmit.isPending}
          >
            Submit{" "}
            {onSubmit.isPending ? <Spinner animation="border" size="sm" /> : ""}
          </Button>
        </Col>
        <Col className="d-grid gap-1" xl={6} md={12}>
          <Button
            className="mb-1"
            variant="danger"
            onClick={handleDiscardChanges}
            disabled={onSubmit.isPending}
          >
            Discard
          </Button>
        </Col>
      </Row>
      <Row>
        <Col style={{ pointerEvents: onSubmit.isPending ? "none" : "auto" }}>
          {/* Only while the FIRST page is outstanding. Every further page turns
              the hook pending again under a new key, and unmounting the table
              for each of them would flash the screen through Loading once per
              page. */}
          {isPendingFirstRiskAllocationsPage ? (
            <Loading />
          ) : (
            <Table
              table={table}
              RowComp={roles.includes("Admin") ? EditableTableRow : undefined}
              onSave={onConfirmRow}
              onEdit={onEditRow}
              onDiscard={onDiscardRow}
              editComp={(row) => (
                <ActionButton
                  type="button"
                  onClick={() => onResetRow(row)}
                  disabled={row.original.growth === row.original.defaultRatio}
                  variant="icon"
                  label="Reset"
                  icon="/icons/redo.svg"
                />
              )}
            />
          )}
        </Col>
      </Row>
    </TabContainerWithTabs>
  );
};

export default RiskAllocationsAssetClass;
